import { createSlice, PayloadAction } from '@reduxjs/toolkit';
import { Article, ArticleType } from '@wix/answers-api';

import { getIsArticleHidden } from '../../util/hiddenArticles';
import { renderModel } from '../../util/renderModel';
import { CategoryItemType } from '../api/getCategories';
import { loadArticleById, loadArticleByUri } from '../thunks/articles.thunk';
import { ErrorStatus, IHelpCenterState, PresentedState } from './types';

const isFullWidth = renderModel.isFullWidth();

const initialState: IHelpCenterState = {
  article: {
    article: null,
    articleHeadingVisible: false,
    error: null,
    isLoading: false,
    isArticleHidden: false,
  },
  currentCategory: null,
  categories: [],
  categoryArticles: [],
  normalizedCategories: {},
  fullPageSidebarOpened: false,
  fullPageSidebarExtended: false,
  searchedText: '',
  searchSessionId: undefined,
  searchInputType: undefined,
  isSearchFocus: false,
  currentPresentedState: undefined,
  // if it's ContactApp there is no previous state.
  // Otherwise, users see Widget Button(Anchor Button or Help Button in the header)
  previousPresentedState: isFullWidth ? undefined : PresentedState.WidgetButton,
  hasHomeError: false,
};

// const getNormalizedCategories = (
//   categories: CategoryItemType[],
// ): Record<CategoryItemType['id'], CategoryItemType> => {
//   let categoriesRecord: Record<CategoryItemType['id'], CategoryItemType> = {};

//   (categories ?? []).forEach((category) => {
//     categoriesRecord[category.id] = category;

//     if (category.children) {
//       categoriesRecord = {
//         ...categoriesRecord,
//         ...getNormalizedCategories(category.children),
//       };
//     }
//   });

//   return categoriesRecord;
// };

const getNormalizedCategories = (
  categories: CategoryItemType[],
): Record<CategoryItemType['id'], CategoryItemType> => {
  const normalizedCategories: Record<CategoryItemType['id'], CategoryItemType> = {};

  const normalize = (items: CategoryItemType[]) => {
    items.forEach((category) => {
      normalizedCategories[category.id] = { ...category };
      if (category.children) {
        normalize(category.children);
      }
    });
  };

  normalize(categories ?? []);

  for (const categoryId in normalizedCategories) {
    let currentCategory = normalizedCategories[categoryId];
    let mainParent: CategoryItemType | undefined = currentCategory;

    while (currentCategory?.parentId && normalizedCategories[currentCategory.parentId]) {
      mainParent = normalizedCategories[currentCategory.parentId];
      currentCategory = normalizedCategories[currentCategory.parentId];
    }

    normalizedCategories[categoryId] = {
      ...normalizedCategories[categoryId],
      mainParentId: mainParent?.id ?? categoryId,
    };
  }

  return normalizedCategories;
};
export const helpCenterSlice = createSlice({
  name: 'helpCenter',
  initialState,
  reducers: {
    setCategories: (state, action) => {
      state.categories = action.payload ?? [];
      state.normalizedCategories = getNormalizedCategories(action.payload);
    },
    setCategoryArticles: (state, action: PayloadAction<Article[]>) => {
      state.categoryArticles = action.payload;
    },
    addCategoryArticles: (state, action: PayloadAction<Article[]>) => {
      state.categoryArticles = [...state.categoryArticles, ...action.payload];
    },
    setCurrentCategory: (state, action) => {
      state.article.article = null;
      state.currentCategory = action.payload;
    },
    setCurrentArticle: (
      state,
      action: PayloadAction<{ id: string; title: string; categoryId?: string; type?: ArticleType }>,
    ) => {
      state.currentCategory = null;
      // TODO: fix article storing(in some cases we set only id, title and categoryId to show article title immediately with loader)
      // @ts-expect-error
      state.article.article = action.payload;
    },
    setArticleHeadingVisible: (state, action) => {
      state.article.articleHeadingVisible = action.payload;
    },
    setArticleError: (state, action) => {
      state.article.error = action.payload;
    },
    setSidebarOpened: (state, action: PayloadAction<boolean>) => {
      state.fullPageSidebarOpened = action.payload;
    },
    setSidebarExtended: (state, action: PayloadAction<boolean>) => {
      state.fullPageSidebarExtended = action.payload;
    },
    setSearchedText: (state, action: PayloadAction<string>) => {
      state.searchedText = action.payload;
    },
    setSearchSessionId: (state, action: PayloadAction<string>) => {
      state.searchSessionId = action.payload;
    },
    setSearchInputType: (state, action: PayloadAction<string | undefined>) => {
      state.searchInputType = action.payload;
    },
    setPresentedState: (state, action: PayloadAction<PresentedState>) => {
      // if current state is presented set it as a previous state
      if (state.currentPresentedState) {
        state.previousPresentedState = state.currentPresentedState;
      }
      state.currentPresentedState = action.payload;
    },
    setIsSearchFocus: (state, action: PayloadAction<boolean>) => {
      state.isSearchFocus = action.payload;
    },
    setHomeError: (state, action: PayloadAction<boolean>) => {
      state.hasHomeError = action.payload;
    },
  },
  extraReducers: (builder) => {
    builder
      .addCase(loadArticleById.pending, (state) => {
        state.article.isLoading = true;
        state.article.error = null;
      })
      .addCase(loadArticleById.fulfilled, (state, action) => {
        state.currentCategory = null;
        state.article.article = action.payload;
        state.article.isLoading = false;
        state.article.articleHeadingVisible = false;
        state.article.isArticleHidden = getIsArticleHidden(action.payload?.labels);
        state.article.error = null;
      })
      .addCase(loadArticleById.rejected, (state, action) => {
        state.article.isLoading = false;
        state.article.articleHeadingVisible = false;
        state.article.error = action.payload || ErrorStatus.ServerError;
      });

    builder
      .addCase(loadArticleByUri.pending, (state) => {
        state.article.isLoading = true;
        state.article.error = null;
      })
      .addCase(loadArticleByUri.fulfilled, (state, action) => {
        state.currentCategory = null;
        state.article.article = action.payload;
        state.article.isLoading = false;
        state.article.articleHeadingVisible = false;
        state.article.isArticleHidden = getIsArticleHidden(action.payload?.labels);
        state.article.error = null;
      })
      .addCase(loadArticleByUri.rejected, (state, action) => {
        state.article.isLoading = false;
        state.article.articleHeadingVisible = false;
        state.article.error = action.payload || ErrorStatus.ServerError;
      });
  },
});
